import React, {Fragment, useEffect} from 'react';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import valid from 'card-validator';
import { connect } from 'react-redux';
import { PrimaryButton, SubHeader, FieldLayoutTwoColumn} from 'cstar-react-primitives';

import { useFormatMessage } from '../../../../intl/intlHooks';
import { useOrder } from '../../../../mapi/OrderProvider';
import { cashbotName, Toggle } from '../../../core';
import {
    CREDIT_CARD_NUMBER,
    CREDIT_CARD_TYPE,
    ccDefaultValues,
    CCNumberFormikField,
    CCExpMonthFormikField,
    CCExpYearFormikField,
    CCSecurityCodeFormikField
} from './CreditCardFields';
import {
    EmailFormikField,
    PhoneFormikField,
    PaymentAddressForm,
    defaultAddressFieldValues,
    FullNameFormikField
} from '../../../address/AddressFormikFields';
import { getValidationSchema } from './creditCardValidation';
import { braintreeTypeMap } from './braintreeUtils';
import { creditCardFormMessages } from './creditCardFormMessages';
import { creditCardMessages as msgs } from './creditCardMessages';
import { CreditCardImagesDisplay } from './CreditCardImagesDisplay';
import {
    ADDRESS_FIRSTNAME,
    ADDRESS_LASTNAME,
    ADDRESS_FULLNAME,
    ADDRESS_STREET1,
    ADDRESS_CITY,
    ADDRESS_STATE,
    ADDRESS_POSTCODE
} from '../../../../address';

import { fullFirstAndLastMiddleOptional } from '../../../../address/addressValidation';
import {TermsAgreementInfoField} from "../../../forms/fields/TermsAgreementInfoField";
import { getPhysicalQuantity, itemsHaveShipping } from '../../../../lineItem/lineItemSelectors';
import { allAddresses } from '../../../../address/addressSelectors';
import { AddressSelect } from '../../../address/AddressSelect';
import { useUsers } from '../../../../user/UserContext';
import { useSettings } from '../../../../mall_settings/SettingsProvider';
import PointsNotice from '../../../loyalty/PointsNotice';
import {LoyaltyProvider} from "../../../loyalty/LoyaltyProvider";
import { SAVE_PAYMENT, SavePaymentField } from "../storedPayments/StoredPaymentFields";

export const InnerCreditCardForm = ({
    acceptedCreditCardTypes,
    handleSubmit,
    values,
    setFieldValue,
    physicalQuantity,
    addresses,
    isShippingValid,
}) => {
    const formatMessage = useFormatMessage();
    const [savedAddress, setSavedAddress] = React.useState(false);
    const isPhysicalLineItemInCart = () => physicalQuantity > 0;
    const {userData, fetchError} = useUsers();
    const {orderStatusProcessing, orderHasStatusError} = useOrder();

    useEffect(() => {
        setFieldValue('isShippingValid', isShippingValid);
    }, [isShippingValid]);
    useEffect(() => {
        const validator = valid.number(values[CREDIT_CARD_NUMBER]);
        if (validator.card && validator.card.type) {
            setFieldValue(CREDIT_CARD_TYPE, braintreeTypeMap[validator.card.type]);
        } else {
            setFieldValue(CREDIT_CARD_TYPE, '');
        }
    }, [values[CREDIT_CARD_NUMBER]]);
    useEffect(() => {
        const nameParts = values[ADDRESS_FULLNAME].match(fullFirstAndLastMiddleOptional);
        if (nameParts) {
            setFieldValue(ADDRESS_FIRSTNAME, nameParts[1], false);
            setFieldValue(ADDRESS_LASTNAME, nameParts[2], false);
        }
    }, [values[ADDRESS_FULLNAME]]);
    useEffect(() => {
        setFieldValue(ADDRESS_STREET1, savedAddress ? savedAddress[ADDRESS_STREET1] : '');
        setFieldValue(ADDRESS_CITY, savedAddress ? savedAddress[ADDRESS_CITY] : '');
        setFieldValue(ADDRESS_STATE, savedAddress ? savedAddress[ADDRESS_STATE] : '');
        setFieldValue(ADDRESS_POSTCODE, savedAddress ? savedAddress[ADDRESS_POSTCODE] : '');
    }, [savedAddress]);

    const handleSavedAddressChange = useSavedAddress => {
        setSavedAddress(useSavedAddress ? addresses[0] : false);
    };
    const addressBookClickHandler = selectedAddress => {
        setSavedAddress(selectedAddress);
    };
    return (
        <form onSubmit={handleSubmit}>
            <Fragment>
                <fieldset>
                    <CCNumberFormikField label={formatMessage(msgs.numberLabel)} />
                    <CreditCardImagesDisplay
                        cardTypes={acceptedCreditCardTypes}
                        selectedCard={values[CREDIT_CARD_TYPE]}
                    />
                    <FieldLayoutTwoColumn>
                        <CCExpMonthFormikField label={formatMessage(msgs.expiryMonthLabel)} />
                        <CCExpYearFormikField label={formatMessage(msgs.expiryYearLabel)} />
                    </FieldLayoutTwoColumn>
                    <FieldLayoutTwoColumn>
                        <CCSecurityCodeFormikField label={formatMessage(msgs.securityCodeLabel)} />
                    </FieldLayoutTwoColumn>
                </fieldset>

                <fieldset>
                    {!fetchError && Object.keys(userData).length > 0 && userData.storedPayments &&
                    (<SavePaymentField clickHandler={val => setFieldValue(SAVE_PAYMENT, val)} />)}
                </fieldset>

                <fieldset>
                    <legend>
                        <SubHeader heading={formatMessage(creditCardFormMessages.billingAddressTitle)} />
                    </legend>
                    <FullNameFormikField label={formatMessage(creditCardFormMessages.creditCardNameLabel)} />
                    {isPhysicalLineItemInCart() && (
                        <Toggle
                            labelOnLeft
                            name="isSavedAddress"
                            toggleClickHandler={handleSavedAddressChange}
                            label={formatMessage(creditCardFormMessages.useShippingAddress)}
                        />
                    )}
                    {savedAddress ? (
                        <AddressSelect
                            addresses={addresses}
                            clickHandler={addressBookClickHandler}
                            selectedHashKey={savedAddress.hashKey}
                        />
                    ) : (
                        <PaymentAddressForm />
                    )}
                </fieldset>

                <fieldset>
                    <legend>
                        <SubHeader heading={formatMessage(creditCardFormMessages.yourContactInfoHeader)} />
                    </legend>
                    <EmailFormikField label={formatMessage(creditCardFormMessages.purchaserEmailLabel)} />
                    <PhoneFormikField label={formatMessage(creditCardFormMessages.phoneNumberLabel)} />
                </fieldset>
                <LoyaltyProvider>
                    <PointsNotice />
                </LoyaltyProvider>
                <TermsAgreementInfoField />
                <div className="checkout-actions">
                    <PrimaryButton
                        atts={{disabled: (orderStatusProcessing && !orderHasStatusError) }}
                        type="submit"
                        cashbotName={cashbotName}
                        btnText={formatMessage(creditCardFormMessages.checkoutButtonText)}
                    />
                </div>
            </Fragment>
        </form>
    );
};

InnerCreditCardForm.propTypes = {
    acceptedCreditCardTypes: PropTypes.array.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    values: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    isShippingValid: PropTypes.bool.isRequired,
    physicalQuantity: PropTypes.number.isRequired,
    addresses: PropTypes.array.isRequired
};

const mapStateToProps = state => ({
    isShippingValid: itemsHaveShipping(state),
    physicalQuantity: getPhysicalQuantity(state),
    addresses: allAddresses(state),
});

export const CreditCardForm = connect(mapStateToProps)(InnerCreditCardForm);
/* eslint react/no-children-prop: 0 */
export const CreditCardFormik = () => {
    const { placeOrder } = useOrder();
    const { userData, fetching } = useUsers();
    const { americanExpress, discoverCard, masterCard, visa } = useSettings();
    const ccSetingsMap = {
        visa,
        mastercard: masterCard,
        discovercard: discoverCard,
        americanexpress: americanExpress
    };
    const acceptedCreditCardTypes = Object.keys(ccSetingsMap).filter(ccType => ccSetingsMap[ccType]);
    const initialValues = {
        ...defaultAddressFieldValues,
        ...ccDefaultValues,
        paymentMethod: 'creditCard',
        [SAVE_PAYMENT]: false,
        currencyCode: 'USD',
        email: userData.email || '',
        fullName: userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '',
    };
    const submitHandler = (values) => {
        placeOrder(values);
    };

    return fetching && !userData ? (
        <Fragment />
    ) : (
        <Formik
            onSubmit={submitHandler}
            initialValues={initialValues}
            validationSchema={getValidationSchema(acceptedCreditCardTypes, useFormatMessage())}
            children={formProps => <CreditCardForm {...formProps} acceptedCreditCardTypes={acceptedCreditCardTypes} />}
        />
    );
};

export default CreditCardFormik;
